import React, { type FC, useState, useEffect } from "react";
import { type FormikValues, useFormik } from 'formik';
import moment from "moment";
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import InfoIcon from '@mui/icons-material/Info';
import { useSnackbar } from "notistack";

import { Button, DateInput, FileUpload } from 'components/Shared/Inputs';
import { confirm } from 'components/Shared/ConfirmDialog';
import { DATE_FORMATS } from "hooks/useTimezoneUtils";
import { type LiabilityInsurance } from "components/ProviderProfile/interfaces";
import { ApiError } from "api/error/ApiError";
import { useAddInsurance, useReplaceInsurance, useUpdateInsurance } from "api/queries/credentialsQuery";

interface LiabilityInsuranceModalProps {
    providerId: number
    liabilityInsurance?: LiabilityInsurance
    open: boolean
    setModalOpen: (modalOpen: boolean) => void
    viewMode?: 'new' | 'renew' | 'upload-only'
    cachePendingUpdate?: boolean
    setCachePendingUpdate?: (pending: boolean) => void
    onUpdated: (liabilityInsurance: LiabilityInsurance) => void
    refreshAttachments?: () => void
}

interface LiabilityInsuranceFormValues {
    expiryDate: string,
    onFile: boolean
}

export const LiabilityInsuranceModal: FC<LiabilityInsuranceModalProps> = (props) => {
    const { providerId, setModalOpen, open, liabilityInsurance, cachePendingUpdate, setCachePendingUpdate, onUpdated, viewMode = 'upload-only', refreshAttachments } = props;

    const { t } = useTranslation('providerprofile', { useSuspense: false });
    const { t: t_common } = useTranslation('common', { useSuspense: false });

    const { enqueueSnackbar } = useSnackbar();

    const [requestPending, setRequestPending] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File|null>(null);
    const [fileError, setFileError] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [errorCode, setErrorCode] = useState('');

    const resetAndCloseModal = () => {
        formik.resetForm();
        setSelectedFile(null);
        setUploadError('');
        setErrorCode('');
        setModalOpen(false);
    };
    
    const showSuccessMessage = (message: string) => {
        enqueueSnackbar(message, { variant: "success" });
        setCachePendingUpdate?.(true);
        refreshAttachments?.();
        resetAndCloseModal();
    };
 
    const onError = (error: Error) => {
        if (error instanceof ApiError && error.isBusinessError) {
            setErrorCode(error.code);
        } else {
            setUploadError(`${t('licenses_registrations__doc_upload_failed')}: ${error.message}`);
        }
        return error;
    }

    const { mutateAsync: updateInsuranceDoc } = useUpdateInsurance({
        onError,
        onSuccess: () => showSuccessMessage(t('licenses_registrations__doc_uploaded_successfully'))
    });
    const { mutateAsync: addLiabilityInsurance } = useAddInsurance({
        onError,
        onSuccess: () => showSuccessMessage(t('liability_insurance__added_successfully'))
    });
    const { mutateAsync: renewLiabilityInsurance } = useReplaceInsurance({
        onError,
        onSuccess: () => showSuccessMessage(t('liability_insurance__renewed_successfully'))
    });

    useEffect(() => {
        formik.resetForm({
            values: {
                expiryDate: '',
                onFile: false
            }
        });
    }, [liabilityInsurance]);

    const formik = useFormik<LiabilityInsuranceFormValues>({
        initialValues: {
            expiryDate: viewMode === 'upload-only' ? liabilityInsurance?.expiryDate ?? '' : '',
            onFile: liabilityInsurance?.onFile ?? false
        },
        onSubmit: doSubmit
    });

    const apiMethods = {
        new: addLiabilityInsurance,
        renew: renewLiabilityInsurance,
        'upload-only': updateInsuranceDoc
    };

    async function doSubmit(values: FormikValues) {
        formik.validateForm();

        if (formik.isValid) {
            setRequestPending(true);
            try {
                const response = await (apiMethods[viewMode])({
                    expiryDate: values.expiryDate || liabilityInsurance?.expiryDate,
                    file: selectedFile!,
                    providerId: providerId.toString()
                }).finally(() => setRequestPending(false));
                if (response?.providerDocument) {
                    setCachePendingUpdate?.(true);
                    onUpdated({
                        onFile: response.providerDocument?.docOnFile === "Y",
                        expiryDate: response.providerDocument?.expiryDate ?? null
                    });
                    refreshAttachments?.();
                }
            } catch (error) {
                setRequestPending(false);
                console.error(error);
            }
        }
    };

    const hasChanges = formik.dirty 
        && (formik.values.expiryDate !== liabilityInsurance?.expiryDate
            || formik.values.onFile !== liabilityInsurance?.onFile);

    // const editorLoading = !commonTranslationsReady;
    const formInvalid = viewMode === 'new' && (!formik.isValid || !hasChanges);
    const hasFileErrors = fileError || !selectedFile || !!errorCode || !!uploadError;
    const cancelDisabled = cachePendingUpdate || requestPending;
    const submitDisabled = cancelDisabled || !formik.values.onFile || formInvalid || hasFileErrors;

    const checkboxDisabeld = formInvalid || hasFileErrors;

    const handleUploadOnly = async () => {
        return doSubmit(formik.values);
    }

    const handleCancelChanges = async () => {
        if (viewMode !== 'upload-only' && hasChanges) {
            const dialogProps = {
                title: t('common__discard_changes_dialog_title'),
                message: t('common__discard_changes_dialog_text')
            }
            if (await confirm(dialogProps)) {
                resetAndCloseModal();
            }
        } else {
            resetAndCloseModal();
        }
    }
    
    const handleExpiryDateChange = (date) => {
        date &&
        formik.setFieldValue('expiryDate', date.format('YYYY-MM-DD'));
    }

    const handleFileSelect = (file: File | null) => {
        setUploadError('');
        setFileError(false);
        setErrorCode('');
        formik.setFieldValue('onFile', false);
        setSelectedFile(file);
    }

    const dialogTitlesByViewMode = {
        new: t('liability_insurance__add_new'),
        renew: t('liability_insurance__renew'),
        'upload-only': t('licenses_registrations__upload_doc'),
    }

    return (
        <Modal open={open} className="dialog_wrapper license_modal" role="dialog" aria-modal aria-labelledby="license_dialog_title" aria-describedby="license_dialog_content">
            <div className='dialog_surface'>
                <Container maxWidth="md">  
                    <div className="dialog_title">
                        <h2 id="liability_insurance_dialog_title" style={{ fontWeight: '400' }}>
                            {dialogTitlesByViewMode[viewMode]}
                        </h2>
                        {viewMode !== 'upload-only' && <p style={{ marginBottom: '24px' }}>{t('liability_insurance__instructions')}</p> }
                        {viewMode === 'renew' && (liabilityInsurance?.attachments?.length ?? 0) > 0 && (
                            <Alert className="upload_limit_warning" variant="standard" color="info" icon={<InfoIcon color="primary" />}>
                                {t('licenses_registrations__renew_warning')}
                            </Alert>
                        )}
                    </div>
                    <div id="liability_insurance_dialog_content">
                        <Grid className="credentialing_sub_section_form" container component='form' onSubmit={formik.handleSubmit} spacing={0}>
                            <>
                                <Grid item xl={12} xs={12}>
                                    {
                                        viewMode !== 'upload-only' &&
                                        <Grid item xl={12} xs={12}>
                                            <DateInput 
                                                value={moment(formik.values.expiryDate, DATE_FORMATS.ISO)}
                                                label={t('liability_insurance__expiry_date_label')}
                                                className="provider_details_input"
                                                onChange={handleExpiryDateChange}
                                            />
                                        </Grid>
                                    }
                                    <Grid item xl={12} xs={12}>
                                        <FileUpload onFileSelect={handleFileSelect} onFileError={setFileError} uploadError={uploadError} uploadErrorCode={errorCode} />
                                    </Grid>
                                    <Grid item xl={12} xs={12}>
                                        <FormControlLabel disabled={checkboxDisabeld} label={t('liability_insurance__comfirm_valid_doc')} control={
                                            <Checkbox
                                                checked={formik.values.onFile}
                                                onChange={formik.handleChange}
                                                name="onFile"
                                                color="primary"
                                                disabled={checkboxDisabeld}
                                            />
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        </Grid>
                    </div>
                    <div className="dialog_actions_wrapper">
                        <Button
                            busy={requestPending || cachePendingUpdate}
                            busyText={t_common('saving')}
                            type="submit"
                            disabled={submitDisabled}
                            onClick={viewMode==='upload-only' ? handleUploadOnly : formik.submitForm}
                        >
                            {t_common('save')}
                        </Button>
                        <Button type="button" variant="text" disabled={cancelDisabled} onClick={handleCancelChanges}>
                            {t_common('cancel')}
                        </Button>
                    </div>
                </Container>
            </div>
        </Modal>
    );
}